import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, getToken, setToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(getToken() ? null : false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken();
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side logout is best-effort — even if the cookie-clear call
      // fails (offline, server down, etc.) we still want to drop the
      // local token + user state below.
      console.warn("Server logout failed; clearing local session anyway", err);
    }
    clearToken();
    setUser(false);
  };

  // Memoise the context value so consumers don't re-render every time
  // AuthProvider re-renders. `login` and `logout` are recreated each render
  // but they only close over setUser (stable) so referential equality across
  // renders is fine for our use here.
  const value = useMemo(() => ({ user, login, logout, refresh }), [user, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
